/**
 * Vendor Invites Component
 * 
 * Allows customer admins to create, view, resend, and revoke vendor invites.
 * Part of the Admin Panel (admin-level access only).
 */

import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { useTheme } from '../context/ThemeContext';
import { Mail, Building2, Calendar, Send, X, Plus, AlertCircle, CheckCircle, Clock } from 'lucide-react';

export default function VendorInvites() {
  const { isDark } = useTheme();
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [actionLoading, setActionLoading] = useState({});

  const [formData, setFormData] = useState({
    vendorEmail: '',
    vendorCompanyName: '',
    vendorPhone: '',
    expiresInDays: 7
  });

  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    loadInvites();
  }, []);

  const loadInvites = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/api/vendors/invites');
      setInvites(Array.isArray(response) ? response : []);
    } catch (err) {
      console.error('Failed to load vendor invites:', err);
      setError(err.data?.message || err.message || 'Failed to load vendor invites.');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.vendorEmail.trim()) {
      errors.vendorEmail = 'Vendor email is required.';
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.vendorEmail.trim())) {
        errors.vendorEmail = 'Invalid email format.';
      }
    }

    if (!formData.vendorCompanyName.trim()) {
      errors.vendorCompanyName = 'Vendor company name is required.';
    }

    if (formData.expiresInDays !== undefined) {
      const days = parseInt(formData.expiresInDays, 10);
      if (isNaN(days) || days < 1 || days > 30) {
        errors.expiresInDays = 'Expiration must be between 1 and 30 days.';
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const response = await api.post('/api/vendors/invites', {
        vendorEmail: formData.vendorEmail.trim(),
        vendorCompanyName: formData.vendorCompanyName.trim(),
        vendorPhone: formData.vendorPhone.trim() || undefined,
        expiresInDays: parseInt(formData.expiresInDays, 10)
      });

      setSuccess(`Invite created successfully! The invite link has been generated.`);
      // Copy invite URL to clipboard if possible
      if (navigator.clipboard && response.inviteUrl) {
        navigator.clipboard.writeText(response.inviteUrl).catch(() => {
          // Ignore clipboard errors
        });
      }
      setShowModal(false);
      setFormData({
        vendorEmail: '',
        vendorCompanyName: '',
        vendorPhone: '',
        expiresInDays: 7
      });
      setFormErrors({});
      await loadInvites();
    } catch (err) {
      console.error('Failed to create invite:', err);
      setError(err.data?.message || err.message || 'Failed to create vendor invite.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async (inviteId) => {
    setActionLoading(prev => ({ ...prev, [inviteId]: true }));
    setError('');
    setSuccess('');

    try {
      const response = await api.post(`/api/vendors/invites/${inviteId}/resend`);
      setSuccess(`Invite resent successfully! A new invite link has been generated.`);
      // Copy invite URL to clipboard if possible
      if (navigator.clipboard && response.inviteUrl) {
        navigator.clipboard.writeText(response.inviteUrl).catch(() => {
          // Ignore clipboard errors
        });
      }
      await loadInvites();
    } catch (err) {
      console.error('Failed to resend invite:', err);
      setError(err.data?.message || err.message || 'Failed to resend invite.');
    } finally {
      setActionLoading(prev => ({ ...prev, [inviteId]: false }));
    }
  };

  const handleRevoke = async (inviteId) => {
    if (!window.confirm('Are you sure you want to revoke this invite? This action cannot be undone.')) {
      return;
    }

    setActionLoading(prev => ({ ...prev, [inviteId]: true }));
    setError('');
    setSuccess('');

    try {
      await api.delete(`/api/vendors/invites/${inviteId}`);
      setSuccess('Invite revoked successfully.');
      await loadInvites();
    } catch (err) {
      console.error('Failed to revoke invite:', err);
      setError(err.data?.message || err.message || 'Failed to revoke invite.');
    } finally {
      setActionLoading(prev => ({ ...prev, [inviteId]: false }));
    }
  };

  const getStatusBadge = (status, expiresAt) => {
    const now = new Date();
    const expires = expiresAt ? new Date(expiresAt) : null;
    const isExpired = expires && expires < now;

    if (status === 'accepted') {
      return (
        <span className={`px-2 py-1 rounded-full text-xs bg-green-900/30 text-green-400 flex items-center gap-1`}>
          <CheckCircle className="w-3 h-3" />
          Accepted
        </span>
      );
    }
    if (status === 'revoked') {
      return (
        <span className={`px-2 py-1 rounded-full text-xs bg-red-900/30 text-red-400 flex items-center gap-1`}>
          <X className="w-3 h-3" />
          Revoked
        </span>
      );
    }
    if (status === 'expired' || isExpired) {
      return (
        <span className={`px-2 py-1 rounded-full text-xs bg-gray-700 text-gray-300 flex items-center gap-1`}>
          <Clock className="w-3 h-3" />
          Expired
        </span>
      );
    }
    if (status === 'pending') {
      return (
        <span className={`px-2 py-1 rounded-full text-xs bg-blue-900/30 text-blue-400 flex items-center gap-1`}>
          <Clock className="w-3 h-3" />
          Pending
        </span>
      );
    }
    return (
      <span className={`px-2 py-1 rounded-full text-xs ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
        {status}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className={`rounded-xl overflow-hidden border ${isDark ? 'bg-gray-800/30 border-gray-700' : 'bg-white border-gray-200 shadow-sm'}`}>
      {/* Header */}
      <div className={`p-6 border-b flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
        <div>
          <h2 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Vendor Invites</h2>
          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Invite vendors to join your organization</p>
        </div>
        <button
          onClick={() => {
            setShowModal(true);
            setError('');
            setSuccess('');
            setFormErrors({});
          }}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
            isDark
              ? 'bg-blue-600 hover:bg-blue-500 text-white'
              : 'bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-500 hover:to-cyan-400 text-white'
          }`}
        >
          <Plus className="w-4 h-4" />
          Invite Vendor
        </button>
      </div>

      {/* Messages */}
      {error && (
        <div className={`mx-6 mt-4 p-3 rounded-md border flex items-start gap-2 ${
          isDark ? 'bg-red-900/20 border-red-800 text-red-300' : 'bg-red-50 border-red-200 text-red-800'
        }`}>
          <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {success && (
        <div className={`mx-6 mt-4 p-3 rounded-md border flex items-start gap-2 ${
          isDark ? 'bg-green-900/20 border-green-800 text-green-300' : 'bg-green-50 border-green-200 text-green-800'
        }`}>
          <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <span className="text-sm whitespace-pre-wrap">{success}</span>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className={`p-12 text-center ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
          <div className={`w-8 h-8 border-2 border-t-blue-500 rounded-full animate-spin mx-auto mb-4 ${isDark ? 'border-gray-600' : 'border-gray-200'}`}></div>
          Loading invites...
        </div>
      ) : (
        <div className="overflow-x-auto">
          {invites.length === 0 ? (
            <div className={`p-12 text-center ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              <Mail className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No vendor invites yet.</p>
              <p className="text-sm mt-2">Click "Invite Vendor" to create your first invite.</p>
            </div>
          ) : (
            <table className="w-full text-left text-sm">
              <thead className={isDark ? 'bg-gray-900/50 text-gray-400' : 'bg-gray-50 text-gray-500'}>
                <tr>
                  <th className="px-6 py-3 font-medium">Vendor Email</th>
                  <th className="px-6 py-3 font-medium">Company Name</th>
                  <th className="px-6 py-3 font-medium">Status</th>
                  <th className="px-6 py-3 font-medium">Expires</th>
                  <th className="px-6 py-3 font-medium">Created</th>
                  <th className="px-6 py-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className={`divide-y ${isDark ? 'divide-gray-800' : 'divide-gray-100'}`}>
                {invites.map((invite) => (
                  <tr key={invite.id} className={`transition-colors ${isDark ? 'hover:bg-gray-800/30' : 'hover:bg-gray-50'}`}>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Mail className={`w-4 h-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                        <span className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>{invite.vendorEmail}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Building2 className={`w-4 h-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                        <span className={isDark ? 'text-gray-300' : 'text-gray-600'}>{invite.vendorCompanyName}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(invite.status, invite.expiresAt)}
                    </td>
                    <td className={`px-6 py-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                      <div className="flex items-center gap-2">
                        <Calendar className={`w-4 h-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                        <span>{formatDate(invite.expiresAt)}</span>
                      </div>
                    </td>
                    <td className={`px-6 py-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                      {formatDate(invite.createdAt)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {invite.status === 'pending' && (
                          <>
                            <button
                              onClick={() => handleResend(invite.id)}
                              disabled={actionLoading[invite.id]}
                              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors flex items-center gap-1 ${
                                isDark
                                  ? 'bg-blue-900/30 hover:bg-blue-900/50 text-blue-400 disabled:opacity-50'
                                  : 'bg-blue-50 hover:bg-blue-100 text-blue-600 disabled:opacity-50'
                              }`}
                            >
                              <Send className="w-3 h-3" />
                              {actionLoading[invite.id] ? 'Resending...' : 'Resend'}
                            </button>
                            <button
                              onClick={() => handleRevoke(invite.id)}
                              disabled={actionLoading[invite.id]}
                              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors flex items-center gap-1 ${
                                isDark
                                  ? 'bg-red-900/30 hover:bg-red-900/50 text-red-400 disabled:opacity-50'
                                  : 'bg-red-50 hover:bg-red-100 text-red-600 disabled:opacity-50'
                              }`}
                            >
                              <X className="w-3 h-3" />
                              {actionLoading[invite.id] ? 'Revoking...' : 'Revoke'}
                            </button>
                          </>
                        )}
                        {invite.status !== 'pending' && (
                          <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No actions</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Create Invite Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border rounded-xl shadow-2xl w-full max-w-md`}>
            {/* Modal Header */}
            <div className={`p-5 border-b flex justify-between items-center ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Invite Vendor</h3>
              <button 
                onClick={() => {
                  setShowModal(false);
                  setFormData({
                    vendorEmail: '',
                    vendorCompanyName: '',
                    vendorPhone: '',
                    expiresInDays: 7
                  });
                  setFormErrors({});
                  setError('');
                  setSuccess('');
                }}
                className={`${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'} text-xl leading-none`}
              >
                ×
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              {/* Vendor Email */}
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Vendor Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={formData.vendorEmail}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, vendorEmail: e.target.value }));
                    if (formErrors.vendorEmail) {
                      setFormErrors(prev => ({ ...prev, vendorEmail: '' }));
                    }
                  }}
                  className={`w-full px-3 py-2 rounded-md text-sm border ${
                    formErrors.vendorEmail
                      ? isDark
                        ? 'bg-red-900/20 border-red-700 text-red-300'
                        : 'bg-red-50 border-red-300 text-red-900'
                      : isDark
                        ? 'bg-gray-900 border-gray-600 text-gray-100 focus:border-blue-500'
                        : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
                  } focus:outline-none focus:ring-1 focus:ring-blue-500`}
                  placeholder="vendor@example.com"
                />
                {formErrors.vendorEmail && (
                  <p className={`text-xs mt-1 ${isDark ? 'text-red-400' : 'text-red-600'}`}>{formErrors.vendorEmail}</p>
                )}
              </div>

              {/* Vendor Company Name */}
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Vendor Company Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.vendorCompanyName}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, vendorCompanyName: e.target.value }));
                    if (formErrors.vendorCompanyName) {
                      setFormErrors(prev => ({ ...prev, vendorCompanyName: '' }));
                    }
                  }}
                  className={`w-full px-3 py-2 rounded-md text-sm border ${
                    formErrors.vendorCompanyName
                      ? isDark
                        ? 'bg-red-900/20 border-red-700 text-red-300'
                        : 'bg-red-50 border-red-300 text-red-900'
                      : isDark
                        ? 'bg-gray-900 border-gray-600 text-gray-100 focus:border-blue-500'
                        : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
                  } focus:outline-none focus:ring-1 focus:ring-blue-500`}
                  placeholder="Vendor Company Inc."
                />
                {formErrors.vendorCompanyName && (
                  <p className={`text-xs mt-1 ${isDark ? 'text-red-400' : 'text-red-600'}`}>{formErrors.vendorCompanyName}</p>
                )}
              </div>

              {/* Vendor Phone */}
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Vendor Phone <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>(optional)</span>
                </label>
                <input
                  type="tel"
                  value={formData.vendorPhone}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, vendorPhone: e.target.value }));
                  }}
                  className={`w-full px-3 py-2 rounded-md text-sm border ${
                    isDark
                      ? 'bg-gray-900 border-gray-600 text-gray-100 focus:border-blue-500'
                      : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
                  } focus:outline-none focus:ring-1 focus:ring-blue-500`}
                  placeholder="+1 (555) 123-4567"
                />
              </div>

              {/* Expiration Days */}
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Expiration (days) <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>(default: 7)</span>
                </label>
                <input
                  type="number"
                  min="1"
                  max="30"
                  value={formData.expiresInDays}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, expiresInDays: e.target.value }));
                    if (formErrors.expiresInDays) {
                      setFormErrors(prev => ({ ...prev, expiresInDays: '' }));
                    }
                  }}
                  className={`w-full px-3 py-2 rounded-md text-sm border ${
                    formErrors.expiresInDays
                      ? isDark
                        ? 'bg-red-900/20 border-red-700 text-red-300'
                        : 'bg-red-50 border-red-300 text-red-900'
                      : isDark
                        ? 'bg-gray-900 border-gray-600 text-gray-100 focus:border-blue-500'
                        : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
                  } focus:outline-none focus:ring-1 focus:ring-blue-500`}
                />
                {formErrors.expiresInDays && (
                  <p className={`text-xs mt-1 ${isDark ? 'text-red-400' : 'text-red-600'}`}>{formErrors.expiresInDays}</p>
                )}
                <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  Invite will expire after this many days (1-30 days)
                </p>
              </div>

              {/* Modal Footer */}
              <div className="flex justify-end gap-2 pt-4 border-t border-gray-700">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setFormData({
                      vendorEmail: '',
                      vendorCompanyName: '',
                      vendorPhone: '',
                      expiresInDays: 7
                    });
                    setFormErrors({});
                    setError('');
                    setSuccess('');
                  }}
                  className={`px-4 py-2 rounded-md text-sm font-medium ${
                    isDark
                      ? 'bg-gray-700 hover:bg-gray-600 text-gray-200'
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                  }`}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className={`px-4 py-2 rounded-md text-sm font-medium disabled:opacity-50 ${
                    isDark
                      ? 'bg-blue-600 hover:bg-blue-500 text-white'
                      : 'bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-500 hover:to-cyan-400 text-white'
                  }`}
                >
                  {submitting ? 'Creating...' : 'Create Invite'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
